import { useEffect, useRef, useState } from 'react';
import { AxiosPromise } from 'axios';

import { CacheOptions, CacheResult, CacheState } from './cachedDataTypes';
import { CacheContextType, useCache } from './CacheProvider';

const WAIT_TIME = 100;
const TIMEOUT = 3000;

export const useCachedData = <T>(
  cacheAccessor: (context: CacheContextType) => CacheState<T>,
  dataSupplier: () => AxiosPromise<T>,
  options?: CacheOptions
): CacheResult<T> => {
  const cachedDataContext = useCache();
  const cache = cacheAccessor(cachedDataContext);
  const enabled = options?.enabled ?? true;
  const ref = useRef<T | null>(null);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (enabled) {
      fetchData();
    } else {
      clearData();
    }
  }, [enabled]);

  useEffect(() => {
    ref.current = cache.data;
  }, [cache.data]);

  const fetchData = async (force = false) => {
    if (!force && (cache.status === 'fetched' || cache.requestInProgress.current)) return;

    try {
      cache.requestInProgress.current = true;
      cache.setStatus('fetching');
      setError(null);

      const { data } = await dataSupplier();

      // recheck condition in case cache was disabled in meantime
      if (cache.requestInProgress.current) {
        cache.setData(data);
        cache.setStatus('fetched');
        ref.current = data;
      }
    } catch (exception) {
      cache.setData(null);
      cache.setStatus('notFetched');
      ref.current = null;
      setError(exception);
    } finally {
      cache.requestInProgress.current = false;
    }
  };

  const waitForDataAndReturn = async () => {
    let counter = 0;

    while (!ref.current && counter <= TIMEOUT) {
      await wait(WAIT_TIME);
      counter += WAIT_TIME;
    }

    return ref.current;
  };

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const clearData = () => {
    cache.requestInProgress.current = false;
    cache.setStatus('notFetched');
    cache.setData(null);
    ref.current = null;
    setError(null);
  };

  return {
    data: cache.data,
    status: cache.status,
    refresh: (onRefreshed?: () => void) => fetchData(true).then(onRefreshed),
    waitForData: waitForDataAndReturn,
    error,
  };
};
